<script lang="ts">
import { computed, defineComponent } from 'vue';
import { storeToRefs } from 'pinia';
import { useUiStateStore } from '../stores/uiState';
import { buildGameLogFileName, buildGameLogText } from './log/gameLogExport';
import { downloadTextFile } from '../lib/downloadTextFile';
import { useBuildProvenanceStore } from '../stores/buildProvenance';
import { formatProvenanceHeaderLines } from '../diagnostics/buildProvenance';

/**
 * Small fixed-position "Download game log" button, mounted once at the play
 * viewport root beside `<DiagnosticExportButton>`.
 *
 * Why it exists (Jeff feedback): at the end-of-match outcome screen the shared
 * board — and with it the `GameLogPanel` whose "Save" button downloads the log
 * transcript — is collapsed behind the "View final board" toggle (the
 * outcome-primary layout). So on the endgame screen the only reachable download
 * is "Download diagnostics"; the human-readable game-log `.txt` has no button,
 * which reads as a missing log. This fills that gap: it appears at game over
 * (when a non-empty log exists) right above the diagnostics button, so the log
 * can be saved without expanding the board. During play it stays hidden — the
 * `GameLogPanel` Save button already covers that.
 *
 * The log itself is unchanged engine-authored data (`UIState.log`); this is a
 * read-only export, same as the diagnostics button. Per the vue-sfc-loader
 * separate-compile pipeline (D-6512) this SFC uses the
 * `defineComponent({ setup() { return {...} } })` form so the template's
 * non-prop bindings reach `_ctx`.
 */
export default defineComponent({
  name: 'GameLogDownloadButton',
  setup() {
    const store = useUiStateStore();
    const { snapshot } = storeToRefs(store);

    // why: the button is the ENDGAME log-download affordance. It shows only when
    // the match is over (gameOver set) AND there is a log to save — during play
    // the GameLogPanel Save button already offers this, so showing it then would
    // duplicate that control.
    const hasDownloadableLog = computed<boolean>(() => {
      const current = snapshot.value;
      return current !== null && current.gameOver !== undefined && current.log.length > 0;
    });

    /**
     * Builds the match-scoped file name from the `?match=` URL param at the
     * export moment.
     */
    function buildFileName(): string {
      const matchId = new URLSearchParams(window.location.search).get('match');
      // why: Date.now() is a client-layer file-name stamp for the export moment,
      // outside the engine determinism boundary (mirrors the diagnostics export).
      return buildGameLogFileName(matchId, Date.now());
    }

    function onDownloadGameLog(): void {
      const current = snapshot.value;
      if (current === null) {
        return;
      }
      // why: stamp the client build, server build and match-creation time above the
      // transcript (read synchronously from the store the play root loaded); no lines
      // until that load finishes, so the transcript falls back to plain text.
      const headerLines = formatProvenanceHeaderLines(useBuildProvenanceStore().provenance);
      downloadTextFile(buildFileName(), buildGameLogText(current.log, headerLines));
    }

    return { hasDownloadableLog, onDownloadGameLog };
  },
});
</script>

<template>
  <button
    v-if="hasDownloadableLog"
    type="button"
    class="game-log-download-button"
    data-testid="game-log-download-button"
    @click="onDownloadGameLog"
  >
    Download game log
  </button>
</template>

<style scoped>
/* why: mirrors the DiagnosticExportButton fixed-position pill. The bottom-left
   fixed-pill column is a SHARED, hand-coordinated stack — occupied slots (each
   left: 8px, z-index: 9999): DiagnosticExportButton 8px, ViewLoadoutButton 40px
   (shown while `?match=` is present), this "Download game log" 72px, and
   GameLogCopyButton 104px. This button was originally at 40px, which silently
   COLLIDED with ViewLoadoutButton (also 40px, painted on top as the later DOM
   sibling) so it was invisible on every live match — the WP-707 live-verify
   bug. Any new bottom-left pill MUST take the next free slot above 104px. */
.game-log-download-button {
  position: fixed;
  bottom: 72px;
  left: 8px;
  font-size: 12px;
  font-family: monospace;
  padding: 5px 10px;
  color: #f1f5f9;
  background: #334155;
  border: 1px solid #64748b;
  border-radius: 4px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.35);
  cursor: pointer;
  user-select: none;
  /* why: a high z-index keeps it reachable above any game overlay or endgame
     panel, matching the diagnostics button it sits beside. */
  z-index: 9999;
}

.game-log-download-button:hover {
  background: #475569;
  border-color: #94a3b8;
}
</style>
